import { defineStore, acceptHMRUpdate } from 'pinia'
import type { Configuration, TrackingType, ProjectLabel } from '@/api/client'
import { DeviceService } from '@/api/client'
import { type TLanguageCodes } from '@/locales/locales'
import { i18n, isSupportedLanguageCode } from '@/locales/locales'
import { Device } from '@capacitor/device'
import handleRequest from '@/helpers/handleRequest'
import { Settings } from 'luxon'
import type { Site } from '@/api/client'
import { useAuthStore } from './AuthStore'

type TState = {
  language: TLanguageCodes | null
  custom_design_enabled?: boolean
  subscription_active?: boolean
  logo_url?: string
  custom_color?: string | null
  readonly available_hosts: Record<string, { api: string; webview: string }>
  active_dev_host: string
  auth_type?: string
  appVersion: string
  build_date: string
  tracking_type: TrackingType | null
  tracking_interval_seconds: number | null
  type: string
  max_image_width: number
  max_image_height: number
  image_quality: number
  employee_list_mode: Configuration.employee_list_mode | null
  language_was_set: boolean
  company_mode: Configuration.company_mode | null
  company_name: string | null
  company_id: number | null
  device_name: string
  device_id: number | null
  device_os?: string
  user_id: number | null
  push_token_expired: boolean
  sites?: Site[]
  project_labels?: ProjectLabel[]
  activeSiteId: number | null
  device_type: string
  amplitude_api_key?: string
  travel_log_enabled?: boolean
  lastProjectSelectionTab: string
  projectInformationOpenOnProjectPage: boolean
  projectInformationOpenOnConfirmationPage: boolean
  ki_features_enabled?: boolean
  correction_reason_enabled?: boolean
  daily_notes_enabled?: boolean
  clocki_enabled?: boolean
  ai_translations_enabled?: boolean
  ai_translation_was_used_at_least_once: boolean
  nfc_auth_enabled?: boolean
  vacation_seconds_enabled?: boolean
  project_videos_enabled?: boolean
  shift_plans_enabled?: boolean
}

const initialState: TState = {
  language: 'de',
  custom_design_enabled: false,
  subscription_active: true,
  logo_url: '',
  custom_color: '',
  available_hosts: {
    _local: {
      // underscore prefix to avoid conflict with vite
      api: 'https://mobile.clockin.test/v2',
      webview: 'https://office.clockin.test',
    },
    d20: {
      api: 'https://mobile.d20.clockin.dev/v2',
      webview: 'https://office.d20.clockin.dev',
    },
    d21: {
      api: 'https://mobile.d21.clockin.dev/v2',
      webview: 'https://office.d21.clockin.dev',
    },
    d22: {
      api: 'https://mobile.d22.clockin.dev/v2',
      webview: 'https://office.d22.clockin.dev',
    },
    d23: {
      api: 'https://mobile.d23.clockin.dev/v2',
      webview: 'https://office.d23.clockin.dev',
    },
    d24: {
      api: 'https://mobile.d24.clockin.dev/v2',
      webview: 'https://office.d24.clockin.dev',
    },
    d25: {
      api: 'https://mobile.d25.clockin.dev/v2',
      webview: 'https://office.d25.clockin.dev',
    },
    d26: {
      api: 'https://mobile.d26.clockin.dev/v2',
      webview: 'https://office.d26.clockin.dev',
    },
    d27: {
      api: 'https://mobile.d27.clockin.dev/v2',
      webview: 'https://office.d27.clockin.dev',
    },
    d28: {
      api: 'https://mobile.d28.clockin.dev/v2',
      webview: 'https://office.d28.clockin.dev',
    },
    d29: {
      api: 'https://mobile.d29.clockin.dev/v2',
      webview: 'https://office.d29.clockin.dev',
    },
    production: {
      api: 'https://mobile.clockin.de/v2',
      webview: 'https://office.clockin.de',
    },
    'clockin-testing': {
      api: 'https://mobile.clockin-testing.de/v2',
      webview: 'https://office.clockin-testing.de',
    },
  },
  active_dev_host: '_local',
  appVersion: import.meta.env.CLOCKIN_APP_VERSION.release,
  build_date: import.meta.env.BUILD_DATE,
  tracking_type: null,
  tracking_interval_seconds: null,
  type: '',
  max_image_width: 2048,
  max_image_height: 2048,
  image_quality: 80,
  employee_list_mode: null,
  language_was_set: false,
  company_mode: null,
  company_name: null,
  company_id: null,
  device_name: '',
  device_id: null,
  device_os: '',
  user_id: null,
  push_token_expired: false,
  sites: [],
  project_labels: [],
  activeSiteId: null,
  device_type: '',
  travel_log_enabled: false,
  lastProjectSelectionTab: '0',
  projectInformationOpenOnProjectPage: false,
  projectInformationOpenOnConfirmationPage: true,
  ki_features_enabled: false,
  correction_reason_enabled: false,
  daily_notes_enabled: false,
  clocki_enabled: false,
  ai_translation_was_used_at_least_once: false,
  nfc_auth_enabled: false,
  vacation_seconds_enabled: false,
  project_videos_enabled: false,
  shift_plans_enabled: false,
}

const useConfigStore = defineStore('configStore', {
  state: () => {
    return { ...initialState }
  },
  getters: {
    host: (state): string => {
      console.log('Getting HOST for mode', import.meta.env.MODE)
      if (import.meta.env.MODE === 'development') {
        return state.available_hosts[state.active_dev_host].api
      }
      if (import.meta.env.MODE === 'pwa') {
        return (
          'https://' + window.location.hostname.replace('app', 'mobile') + '/v2'
        )
      }
      console.log('Getting HOST for mode', import.meta.env.MODE)
      return state.available_hosts[import.meta.env.MODE].api
    },
    webviewUrl: (state): string => {
      if (import.meta.env.MODE === 'development') {
        return state.available_hosts[state.active_dev_host].webview
      }
      if (import.meta.env.MODE === 'pwa') {
        return 'https://' + window.location.hostname.replace('app', 'office')
      }
      return state.available_hosts[import.meta.env.MODE].webview
    },
    termsUrl: (state): string => {
      // right now only an english and a german version of the terms exist, should be dynamic in the future
      if (state.language === 'de') {
        return 'https://www.clockin.de/agb'
      }
      return 'http://www.clockin.eu/en/legal/general-terms-and-conditions'
    },
    imprintUrl: (state): string => {
      // right now only an english and a german version of the imprint exist, should be dynamic in the future
      if (state.language === 'de') {
        return 'https://www.clockin.de/impressum'
      }
      return 'https://www.clockin.eu/en/legal/legal-notice'
    },
    privacyUrl: (state): string => {
      // right now only an english, spanish and a german version of the terms exist, should be dynamic in the future
      if (state.language === 'de') {
        return 'https://www.clockin.de/datenschutz/'
      }
      if (state.language === 'es') {
        return 'https://www.clockin.eu/es/legal/privacy-policy'
      }
      return 'https://www.clockin.eu/en/legal/privacy-policy'
    },
    appInfo(state): string {
      const regex = /api\.([a-z0-9]+)/i
      const hostShorthand = this.host.match(regex)?.[1] || ''
      return state.appVersion + ' ' + hostShorthand
    },
    subscriptionIsActive(state): boolean {
      return (
        state.subscription_active === true ||
        state.subscription_active === undefined
      )
    },
    activityTrackingEnabled(state): boolean {
      return state.tracking_type === 'activity'
    },
    intervalTrackingEnabled(state): boolean {
      return state.tracking_type === 'interval'
    },
    trackingEnabled(): boolean {
      return this.activityTrackingEnabled || this.intervalTrackingEnabled
    },
    initialState: () => {
      return initialState
    },
    activeSite: (state) => {
      return state.sites?.find((site) => site.id === state.activeSiteId)
    },
  },
  actions: {
    async init() {
      const lang = (await Device.getLanguageCode()).value
      if (!this.language_was_set) {
        await this.setLanguage(lang)
      }
      this.updateAppVersionLabel()
      const deviceInfo = await Device.getInfo()
      this.device_os = deviceInfo.operatingSystem + ' ' + deviceInfo.osVersion
      this.device_type = deviceInfo.model
    },
    updateAppVersionLabel() {
      const versionString = import.meta.env.CLOCKIN_APP_VERSION.release
      if (
        versionString.length > 0 &&
        versionString !== this.$state.appVersion
      ) {
        this.$state.appVersion = versionString
      }
    },
    setLanguage(language: string) {
      if (!isSupportedLanguageCode(language)) {
        language = initialState.language ?? 'de'
      }
      this.$state.language = language as TLanguageCodes
      i18n.locale.value = this.$state.language
      Settings.defaultLocale = language
      this.$state.language_was_set = true
    },

    setActiveSite(id: number) {
      this.$state.activeSiteId = id
    },

    unsetActiveSite() {
      this.$state.activeSiteId = null
    },

    async sync() {
      const data = (await handleRequest(DeviceService.getConfig()))?.data
      if (!data) return

      this.custom_design_enabled = data?.custom_design_enabled || false
      this.logo_url =
        data?.custom_design_enabled && data?.logo ? data?.logo : ''
      this.custom_color = data?.custom_color || ''
      this.type = data?.type || ''
      this.tracking_type = data?.tracking_type || null
      this.tracking_interval_seconds = data?.tracking_interval_seconds || null
      this.employee_list_mode = data?.employee_list_mode || null
      this.company_mode = data?.company_mode || null
      this.company_name = data?.company_name || null
      this.company_id = data?.company_id || null
      this.device_name = data?.device_name || ''
      this.device_id = data?.device_id || null
      this.user_id = data?.user_id || null
      this.push_token_expired = data?.push_token_expired || false
      this.sites = data?.sites || []
      this.project_labels = data?.project_labels || []
      this.amplitude_api_key = data?.amplitude_api_key || undefined
      this.travel_log_enabled = data?.travel_log_enabled || false
      this.ki_features_enabled = data?.ki_features_enabled || false
      this.correction_reason_enabled = data?.correction_reason_enabled || false
      this.daily_notes_enabled = data?.daily_notes_enabled || false
      this.clocki_enabled = data?.clocki_enabled || false
      this.ai_translations_enabled = data?.ai_translations_enabled || false
      this.nfc_auth_enabled = data?.nfc_auth_enabled || false
      this.vacation_seconds_enabled = data?.vacation_seconds_enabled || false
      this.project_videos_enabled = data?.project_videos_enabled || false
      this.shift_plans_enabled = data?.shift_plans_enabled || false

      if (useAuthStore().isTerminalDevice && this.sites.length > 0) {
        this.setActiveSite(this.sites[0].id)
      }
    },
    hydrate(state: string) {
      this.$state = JSON.parse(state)
      void this.setLanguage(
        this.$state.language || this.initialState.language || 'de',
      )
      // TODO merge constant settings and dynamic settings
      this.$state.image_quality = initialState.image_quality
      this.$state.max_image_width = initialState.max_image_width
      this.$state.max_image_height = initialState.max_image_height
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useConfigStore, import.meta.hot))
}
export { useConfigStore }
